import queryString from 'query-string';
import React, { createContext, useReducer, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import reducer from './reducer';
import { ActionType } from './action-types';
import { setFilter, setSort } from './action-creators';
import { Pagination, Row, Payload, Filter, Sort, RequiredField } from 'common/types/admin-page.type';

export interface State<T extends RequiredField> {
  rows: Row<T>[];
  isLoading: boolean;
  rowToEdit: Payload<T> | null;
  rowsToDelete: Row<T>[] | null;
  params: {
    filter: Filter<T>;
    sort: Sort<T>;
    pagination: Pagination | null;
  };
}

function getInitialState<T>(): State<T & RequiredField> {
  return {
    rows: [],
    isLoading: true,
    rowToEdit: null,
    rowsToDelete: null,
    params: {
      filter: {},
      sort: {},
      pagination: null,
    },
  };
}

export function createStateContext<T extends RequiredField>() {
  return createContext<State<T>>(getInitialState());
}

export function createDispatchContext<T extends RequiredField>() {
  return createContext<React.Dispatch<ActionType<T>>>(() => {});
}

interface AdminProviderProps<T extends RequiredField> {
  StateContext: React.Context<State<T>>;
  DispatchContext: React.Context<React.Dispatch<ActionType<T>>>;
}

export const AdminProvider = <T extends RequiredField>(props: React.PropsWithChildren<AdminProviderProps<T>>) => {
  const reduxDispatch = useDispatch();

  const { DispatchContext, StateContext } = props;
  const [state, dispatch] = useReducer(createReducer<T>(), getInitialState<T>());
  const { search } = useLocation();

  useEffect(() => {
    reduxDispatch({ type: 'RESIZE_MAIN' });
    reduxDispatch({ type: 'SET_TOOLBAR_TYPE', payload: 'Admin' });
  }, [reduxDispatch]);

  useEffect(() => {
    const parsed = queryString.parse(search);
    const filter: Filter<T> = {};

    Object.entries(parsed).forEach(([key, value]) => {
      if (typeof value !== 'string') return;
      filter[key as keyof T] = value;
    });

    dispatch(setFilter(filter));
  }, [search]);

  useEffect(() => {
    const parsed = queryString.parse(search);
    const sort: Record<string, string> = {};

    Object.entries(parsed).forEach(([key, value]) => {
      if (typeof value !== 'string') return;
      if (!key.startsWith('sort')) return;
      sort[key] = value;
    });

    dispatch(setSort(sort));
  }, [search]);

  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={state}>{props.children}</StateContext.Provider>
    </DispatchContext.Provider>
  );
};

function createReducer<T extends RequiredField>() {
  return (state: State<T>, action: ActionType<T>) => reducer(state, action);
}
